/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 動く図で選んだ札の元の行を出す。基本設計のページでは図の横の欄に、図だけのページでは図の右上に浮かせて出す
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { bdCell, labelOf, splitRefs } from './bd-defs.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdGraphNode,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';

/** 節を開くリンクを、ほかの小さいボタン（secondary / sm）と同じ見た目にする。 @graph-connects none */
const SECTION_LINK_CLASS =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]';

/** 図の横の欄の見た目。 @graph-connects none */
const PANEL_CARD_CLASS =
  'space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 text-xs';

/**
 * 図の上に浮かせる欄の見た目。幅を決め、長い行は欄の中でスクロールさせ、下の図が少し透ける。
 * @graph-connects none
 */
const FLOATING_CARD_CLASS =
  'w-80 max-h-[calc(100dvh-2rem)] space-y-2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[color-mix(in_srgb,var(--color-bg-surface)_92%,transparent)] p-3 text-xs shadow-lg backdrop-blur-sm';

export interface BdGraphRowProps {
  /** 選んだ札。選んでいなければ null */
  node: BdGraphNode | null;
  /** 全節の定義（札の元の行を読むため） */
  defs: readonly BdArtifactDef[];
  /** 保存済みの節（札の元の行を読むため） */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 節を開く。`sectionHref` があればそちらを使う */
  onOpenSection: ((artifact: BdArtifact) => void) | undefined;
  /** 節を開くリンクの先 */
  sectionHref: ((artifact: BdArtifact) => string) | undefined;
  /** 閉じる */
  onClose: () => void;
}

/**
 * 札の元の節と行。元の行を持たない札（サービスなど）は両方 undefined。
 * @graph-connects bdCell [calls] 行の ID 欄
 */
function rowOf(
  node: BdGraphNode,
  defs: readonly BdArtifactDef[],
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>,
) {
  const ref = node.ref;
  const def = ref === undefined ? undefined : defs.find((d) => d.key === ref.artifact);
  const row =
    ref === undefined
      ? undefined
      : saved[ref.artifact]?.rows.find((r) => bdCell(r, 'id') === ref.rowId);
  return { def, row };
}

/**
 * 札の見出し（節の名前、無ければ札の種類）。
 * @graph-connects none
 */
function useRowHeading(node: BdGraphNode, def: BdArtifactDef | undefined): string {
  const { t } = useTranslation();
  return def === undefined ? t(`bd.graph.kinds.${node.kind}`) : `${def.section} ${def.title}`;
}

/**
 * 札の元の行のすべての欄と、節を開くボタン。行が無ければ札の 2 行目だけを出す。
 * @graph-connects labelOf [calls] 選択肢の表示名
 * @graph-connects splitRefs [calls] 参照の並び
 */
function BdGraphRowBody({
  node,
  def,
  row,
  onOpenSection,
  sectionHref,
}: {
  node: BdGraphNode;
  def: BdArtifactDef | undefined;
  row: ReturnType<typeof rowOf>['row'];
  onOpenSection: BdGraphRowProps['onOpenSection'];
  sectionHref: BdGraphRowProps['sectionHref'];
}) {
  const { t } = useTranslation();
  return (
    <>
      {def === undefined || row === undefined ? (
        node.sub === undefined || node.sub === '' ? null : (
          <p className="text-[var(--color-text-secondary)]">{node.sub}</p>
        )
      ) : (
        <dl className="space-y-1.5 border-t border-[var(--color-border)] pt-2">
          {def.columns.map((column) => {
            const value = bdCell(row, column.key);
            return (
              <div key={column.key}>
                <dt className="text-[var(--color-text-muted)]">{column.label}</dt>
                <dd className="whitespace-pre-wrap break-words text-[var(--color-text-primary)]">
                  {value === ''
                    ? '—'
                    : column.kind === 'choice'
                      ? labelOf(column, value)
                      : column.kind === 'refs'
                        ? splitRefs(value).join(', ')
                        : value}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {def === undefined ? null : sectionHref === undefined ? (
        onOpenSection === undefined ? null : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onOpenSection(def.key);
            }}
          >
            {t('bd.graph.openSection', { title: def.title })}
          </Button>
        )
      ) : (
        <a className={SECTION_LINK_CLASS} href={sectionHref(def.key)}>
          {t('bd.graph.openSection', { title: def.title })}
        </a>
      )}
    </>
  );
}

/**
 * 図の横の欄。選んでいなければ選び方の案内を出す。
 * @graph-connects rowOf [calls] 札の元の行
 * @graph-connects BdGraphRowBody [calls] 行の中身
 */
export function BdGraphRowPanel({
  node,
  defs,
  saved,
  onOpenSection,
  sectionHref,
  onClose,
}: BdGraphRowProps) {
  const { t } = useTranslation();
  if (node === null) {
    return (
      <aside className="rounded-lg border border-dashed border-[var(--color-border)] p-3 text-xs text-[var(--color-text-muted)]">
        {t('bd.graph.selectHint')}
      </aside>
    );
  }
  return (
    <BdGraphRowPanelCard
      node={node}
      defs={defs}
      saved={saved}
      onOpenSection={onOpenSection}
      sectionHref={sectionHref}
      onClose={onClose}
    />
  );
}

/** 選んだ札の欄。 @graph-connects BdGraphRowBody [calls] 行の中身 */
function BdGraphRowPanelCard({
  node,
  defs,
  saved,
  onOpenSection,
  sectionHref,
  onClose,
  className = PANEL_CARD_CLASS,
}: BdGraphRowProps & { node: BdGraphNode; className?: string }) {
  const { t } = useTranslation();
  const { def, row } = rowOf(node, defs, saved);
  const heading = useRowHeading(node, def);
  return (
    <aside aria-label={t('bd.graph.rowAria', { label: node.label })} className={className}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[var(--color-text-muted)]">{heading}</p>
          <p className="break-words text-sm font-semibold text-[var(--color-text-primary)]">
            {node.label}
          </p>
        </div>
        <button
          type="button"
          aria-label={t('bd.graph.closeRow')}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <BdGraphRowBody
        node={node}
        def={def}
        row={row}
        onOpenSection={onOpenSection}
        sectionHref={sectionHref}
      />
    </aside>
  );
}

/**
 * 図だけのページで、選んだ札の行を図の右上に浮かせて出す。下の図は覆わないので、開いたまま
 * 図を動かしたり別の札を押したりできる（押すと中身が替わる）。
 * @graph-connects BdGraphRowPanelCard [calls] 選んだ札の欄
 */
export function BdGraphRowFloating(props: BdGraphRowProps) {
  const { node } = props;
  if (node === null) return null;
  return <BdGraphRowPanelCard {...props} node={node} className={FLOATING_CARD_CLASS} />;
}
